import PropTypes from 'prop-types';
import { map, replace, defaultTo, compose } from 'ramda';
import { connect } from 'react-redux';
import { setPropTypes } from 'recompose';
import { requestPlayerSelfExclusion, requestPlayerTimeOut } from '@rushplay/api-client';

function Exclusion(props) {
  return props.children({
    options: props.options,
    onSubmit: props.onSubmit
  });
}
Exclusion.propTypes = {
  children: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    labelKey: PropTypes.string,
    value: PropTypes.string
  })),
  onSubmit: PropTypes.func
};

function mapStateToProps(state, props) {
  return {
    options: map(function (item) {
      return {
        value: item,
        labelKey: "self-exclusion.options.".concat(replace(' ', '-', item))
      };
    }, defaultTo([], props.values))
  };
}

function mapDispatchToProps(dispatch, props) {
  return {
    onSubmit: function onSubmit(period, password) {
      dispatch(requestPlayerSelfExclusion(period, password, {
        success: props.onSuccess,
        failure: props.onFailure,
        version: 2
      }));
    }
  };
}

var connector = compose(setPropTypes({
  values: PropTypes.arrayOf(PropTypes.string),
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func
}), connect(mapStateToProps, mapDispatchToProps));

function mapStateToProps$1(state, props) {
  return {
    options: map(function (item) {
      return {
        value: item,
        labelKey: "time-out.options.".concat(replace(' ', '-', item))
      };
    }, defaultTo([], props.values))
  };
}

function mapDispatchToProps$1(dispatch, props) {
  return {
    onSubmit: function onSubmit(period, password) {
      dispatch(requestPlayerTimeOut(period, password, {
        success: props.onSuccess,
        failure: props.onFailure,
        version: 2
      }));
    }
  };
}

var connector$1 = compose(setPropTypes({
  values: PropTypes.arrayOf(PropTypes.string),
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func
}), connect(mapStateToProps$1, mapDispatchToProps$1));

/** @module rushplay/compliance/exclusion */
/**
 * Complete self-exclusion
 * @typedef {function} SelfExclusionSubmit
 * @param {("6 months"|"1 year"|"2 years"|"3 years"|"4 years"|"5 years")} period of self exclusion
 * @param {string} [password] Optional for players signed up with Trustly
 */

/**
 * Complete time out
 * @typedef {function} TimeOutSubmit
 * @param {("1 day"|"7 days"|"2 weeks"|"6 weeks")} period of timeout
 * @param {string} [password] Optional for players signed up with Trustly
 */

/**
 * @typedef {Object} SelfExclusionRenderProps
 * @property {Array.<Object>} options Available self exclusion periods
 * @property {SelfExclusionSubmit} onSubmit
 */

/**
 * @typedef {Object} TimeOutRenderProps
 * @property {Array.<Object>} options Available time out periods
 * @property {TimeOutSubmit} onSubmit
 */

/**
 * Render prop component to access and manage self exclusion
 *
 * Expects a one-arg function as children. Argument to function has shape:
 * ```
 * {
 *   onSubmit: PropTypes.function,
 *   options: PropTypes.arrayOf(
 *     PropTypes.shape({
 *       labelKey: PropTypes.string,
 *       value: PropTypes.string,
 *     })
 *   ),
 * }
 *
 * @memberof rushplay/compliance/exclusion
 * @type {ReactComponent}
 * @param {Object} props
 * @param {function(SelfExclusionRenderProps): ReactElement} props.children
 * @param {function} [props.onSuccess]
 * @param {function} [props.onFailure]
 * @example
 * <SelfExclusion
 *   onSuccess={() => alert('success')}
 *   onFailure={() => alert('failure')}
 *   values={['1 day', '7 days', '6 weeks']}
 * >
 *   {exclusion => (
 *     <ul>
 *       {R.map(option => <li>option.labelKey</li>)}, exclusion.options)}
 *     </ul>
 *   )}
 *  </SelfExclusion>
 */

var SelfExclusion = connector(Exclusion);
/**
 * Render prop component to access and manage time out
 *
 * Expects a one-arg function as children. Argument to function has shape:
 * ```
 * {
 *   onSubmit: PropTypes.function,
 *   options: PropTypes.arrayOf(
 *     PropTypes.shape({
 *       labelKey: PropTypes.string,
 *       labelParams: PropTypes.object,
 *       value: PropTypes.string,
 *     })
 *   ),
 * }
 *
 * @memberof rushplay/compliance/exclusion
 * @type {ReactComponent}
 * @param {Object} props
 * @param {function(TimeOutRenderProps): ReactElement} props.children
 * @param {function} [props.onSuccess]
 * @param {function} [props.onFailure]
 * @example
 * <Timeout
 *   onSuccess={() => alert('success')}
 *   onFailure={() => alert('failure')}
 *   values={['1 day', '1 year']}
 * >
 *   {exclusion => (
 *     <ul>
 *       {R.map(option => <li>option.labelKey</li>)}, exclusion.options)}
 *     </ul>
 *   )}
 * </Timeout>
 */

var Timeout = connector$1(Exclusion);

export { SelfExclusion, Timeout };
